// Shared split-screen chrome for every auth surface (login, register,
// forgot-password, set-password). Presentation only — each page renders
// its own form as `children`; this supplies the form column, brand panel,
// and entrance motion.
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { Radio, TrendingUp, ShieldCheck } from 'lucide-react'
import AuthBrandPanel from './AuthBrandPanel'
import BrandMark from '../ui/BrandMark'

const FEATURES = [
  { icon: Radio, label: 'Real-time outbreak alerts across every sector' },
  { icon: TrendingUp, label: 'Climate-health correlation, not just case counts' },
  { icon: ShieldCheck, label: "Role-based access built for Rwanda's health network" },
]

export default function AuthShell({ children }) {
  const reduceMotion = useReducedMotion()

  return (
    <div className="min-h-viewport lg:h-viewport flex flex-col lg:flex-row bg-white lg:overflow-hidden">
      {/* Form column — containerless, no card/border/shadow. `lg:overflow-y-auto`
          lets it scroll internally instead of growing the row past the viewport. */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 sm:px-6 lg:px-12 lg:overflow-y-auto lg:order-2">
        <div className="w-full max-w-[400px]">
          {/* Wordmark — visible here too since the brand panel hides below `lg`.
              `flex justify-center` wrapper, not `mx-auto` (doesn't self-center inline-flex). */}
          <div className="flex justify-center mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2.5 w-fit rounded-2xl link-focus"
            >
              <BrandMark iconClass="h-9" wordmarkClass="text-2xl" />
            </Link>
          </div>

          <motion.div
            initial={reduceMotion ? undefined : { opacity: 0, y: 16 }}
            animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            transition={reduceMotion ? undefined : { duration: 0.4, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        </div>
      </div>

      {/* Shared with RequestAccessPage — the panel carries a load-bearing
          WCAG scrim, so it's one component, not a duplicated copy. */}
      <AuthBrandPanel
        heading="Early warning, before the outbreak spreads."
        subtitle="Climate-linked disease surveillance for Rwanda's health system — from facility reports to national response."
        items={FEATURES}
      />
    </div>
  )
}
